// ============================================================================
// VoiceRoomView.tsx — экран ВНУТРИ голосовой комнаты (волна 2, D7).
//
// Состояние ведёт СЕРВЕР (VoiceRoomService): участники (active/left), роли,
// микрофон (muted_self / muted_by), live-обновления — события шины
// voice.room.joined|left|updated.{roomId}. Медиа — LiveKit (useVoiceRoomMedia,
// только звук). Вход идемпотентен (повторный JoinRoom не создаёт дублей);
// «Заглушить себя» = LeaveRoom → JoinRoom(mutedSelf) — единственный способ
// серверу зафиксировать muted_self (роль при возврате сохраняется).
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowLeft, LogOut, Mic, MicOff, RefreshCw, Volume2 } from 'lucide-react';
import type { VoiceRoom, VoiceRoomMember } from '@zdc/sdk';
import { Button } from '@/components/ui/button';
import { confirmDialog } from '@/lib/dialogs';
import { errorCode, friendlyError, logErrorToJournal, logSwallowed } from '@/lib/errors';
import { eventBusHub } from '@/lib/eventBusHub';
import { createLogger } from '@/lib/logger';
import { useSecurityAccess } from '@/lib/securityRoles';
import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { VoiceRoomMemberRow } from './VoiceRoomMemberRow';
import { useVoiceRoomMedia } from './useVoiceRoomMedia';
import {
  VOICE_ROLE_LABELS,
  compareMembers,
  memberLabel,
  readMutedPref,
  writeMutedPref,
} from './prefs';

const log = createLogger('VoiceRooms.View');

const CODE_PERMISSION_DENIED = 7;
const CODE_NOT_FOUND = 5;

function hasCode(e: unknown, numeric: number, name: string): boolean {
  const code = errorCode(e);
  if (typeof code === 'number') return code === numeric;
  if (typeof code === 'string') {
    return code.trim().toLowerCase().replace(/[\s_-]/g, '') === name;
  }
  return false;
}

export function VoiceRoomView({ roomId, onExit }: { roomId: string; onExit: () => void }) {
  const access = useSecurityAccess();
  const myId = String(useAuthStore((s) => s.user?.id) ?? '');
  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  /** Непоправимое состояние комнаты: удалена другим участником / нет доступа.
   *  Показываем ЯВНЫЙ экран состояния вместо тихого выхода из комнаты. */
  const [fatal, setFatal] = useState<'' | 'deleted' | 'denied'>('');
  const [busy, setBusy] = useState(false);
  const [mutedSelf, setMutedSelf] = useState<boolean>(() => readMutedPref());
  const audioRef = useRef<HTMLDivElement | null>(null);
  /** Идёт собственный выход/пере-вход («Заглушить себя», «Выйти») — события
   *  шины этого окна НЕ должны автоматически возвращать нас в комнату. */
  const selfToggleRef = useRef(false);

  const myMember = room?.members.find((m) => m.entityId === myId);
  const myActive = !!room && myMember?.state === 'active';
  const media = useVoiceRoomMedia(roomId, myActive, mutedSelf, audioRef);

  const showError = (m: string) => useToastStore.getState().showError(m);
  const showWarning = (m: string) => useToastStore.getState().showWarning(m);
  const showSuccess = (m: string) => useToastStore.getState().showSuccess(m);

  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    try {
      let r = await getSDK().voiceRooms.getRoom({ roomId });
      const me = r.members.find((m) => m.entityId === myId);
      if ((!me || me.state !== 'active') && !selfToggleRef.current) {
        r = await getSDK().voiceRooms.joinRoom({ roomId, mutedSelf: readMutedPref() });
      }
      setRoom(r);
      setLoadError('');
    } catch (e) {
      log.error('voice room load', { error: e });
      logErrorToJournal(e, 'Голосовая комната: загрузка комнаты');
      if (hasCode(e, CODE_NOT_FOUND, 'notfound')) {
        // Комнаты больше нет — отпускаем медиа (myActive станет false).
        setRoom(null);
        setFatal('deleted');
        showWarning('Комната не найдена или удалена');
        return;
      }
      if (hasCode(e, CODE_PERMISSION_DENIED, 'permissiondenied')) {
        setFatal('denied');
        setLoadError('Нет доступа к комнате');
        return;
      }
      if (!silent) setLoadError(friendlyError(e));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [roomId, myId, onExit]);

  useEffect(() => {
    void load(false);
  }, [load]);

  // Live-обновления: любой voice.room.* этой комнаты → перечитать состояние.
  useEffect(() => {
    const unsub = eventBusHub.subscribe((ev) => {
      if (!ev.topic.startsWith('voice.room.') || !ev.topic.endsWith(`.${roomId}`)) return;
      if (ev.topic.startsWith('voice.room.deleted.')) {
        showWarning('Комната удалена');
        setRoom(null);
        setFatal('deleted');
        return;
      }
      void load(true);
    });
    return unsub;
  }, [roomId, load, onExit]);

  const toggleSelfMute = async () => {
    if (!room) return;
    const next = !mutedSelf;
    setBusy(true);
    selfToggleRef.current = true;
    try {
      // muted_self фиксирует сервер ТОЛЬКО при входе: выходим и входим снова.
      await getSDK().voiceRooms.leaveRoom({ roomId: room.id });
      const r = await getSDK().voiceRooms.joinRoom({ roomId: room.id, mutedSelf: next });
      setRoom(r);
      setMutedSelf(next);
      writeMutedPref(next);
      try {
        await media.setMicEnabled(!next);
      } catch (e) {
        logSwallowed(e, 'Голосовые комнаты: переключение микрофона');
        showWarning('Не удалось переключить микрофон — проверьте разрешение');
      }
    } catch (e) {
      log.error('voice room self mute', { error: e });
      logErrorToJournal(e, 'Голосовая комната: переключение своего микрофона');
      showError(friendlyError(e));
      // Возврат в комнату с прежней настройкой (если получится).
      selfToggleRef.current = false;
      await load(true);
    } finally {
      selfToggleRef.current = false;
      setBusy(false);
    }
  };

  const leave = async () => {
    setBusy(true);
    selfToggleRef.current = true;
    try {
      await getSDK().voiceRooms.leaveRoom({ roomId });
    } catch (e) {
      log.error('voice room leave', { error: e });
      logErrorToJournal(e, 'Голосовая комната: выход');
      showError(friendlyError(e));
    } finally {
      setBusy(false);
      onExit();
    }
  };

  const changeRole = async (m: VoiceRoomMember, role: string) => {
    if (!room || role === m.role) return;
    const ok = await confirmDialog(
      `Сменить роль участника «${memberLabel(m, myId)}» на «${VOICE_ROLE_LABELS[role] ?? role}»?`,
      'Смена роли',
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await getSDK().voiceRooms.setMemberRole({
        roomId: room.id,
        entityId: m.entityId,
        role,
      });
      setRoom(r);
      showSuccess('Роль изменена');
    } catch (e) {
      log.error('voice room set role', { error: e });
      logErrorToJournal(e, 'Голосовая комната: смена роли участника');
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleMemberMute = async (m: VoiceRoomMember) => {
    if (!room) return;
    const next = !m.mutedBy;
    const ok = await confirmDialog(
      next
        ? `Выключить микрофон участнику «${memberLabel(m, myId)}»?`
        : `Включить микрофон участнику «${memberLabel(m, myId)}»?`,
      'Микрофон участника',
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await getSDK().voiceRooms.muteMember({
        roomId: room.id,
        entityId: m.entityId,
        muted: next,
      });
      setRoom(r);
    } catch (e) {
      log.error('voice room mute member', { error: e });
      logErrorToJournal(e, 'Голосовая комната: микрофон участника');
      showError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const canManage =
    !!room &&
    (access.isAdmin ||
      myId === room.createdBy ||
      (myMember?.role === 'moderator' && myMember?.state === 'active'));

  const members = room ? [...room.members].sort((a, b) => compareMembers(a, b, room.createdBy)) : [];
  const activeCount = room ? room.activeCount : 0;

  const mediaBadge = (() => {
    if (media.status === 'connected') {
      return { text: 'Подключено к голосовой комнате', cls: 'text-green-600' };
    }
    if (media.status === 'connecting') return { text: 'Подключение к медиа…', cls: 'text-muted-foreground' };
    if (media.status === 'unavailable') return { text: 'Медиа не настроены', cls: 'text-amber-600' };
    if (media.status === 'error') return { text: 'Медиа недоступны', cls: 'text-destructive' };
    return { text: 'Медиа не подключены', cls: 'text-muted-foreground' };
  })();

  // ЯВНЫЕ состояния «комната удалена» / «нет доступа» — не тихий выброс в
  // список: пользователь понимает, что произошло, и может вернуться.
  if (fatal) {
    const deleted = fatal === 'deleted';
    return (
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center gap-2 border-b px-3 py-2">
          <Button variant="ghost" size="sm" onClick={() => onExit()} title="К списку комнат">
            <ArrowLeft className="h-4 w-4 mr-1" /> К комнатам
          </Button>
          <div className="min-w-0 flex-1 truncate text-sm font-medium">
            {deleted ? 'Комната удалена' : 'Нет доступа'}
          </div>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="text-sm font-medium">
            {deleted ? 'Эта комната была удалена владельцем' : 'У вас нет доступа к этой комнате'}
          </div>
          <div className="max-w-md text-xs text-muted-foreground">
            {deleted
              ? 'Участники отключены от комнаты. Создайте новую комнату или выберите другую в списке.'
              : 'Доступ к приватной комнате выдаётся по ролям. Попросите владельца добавить вашу роль или выбрать другую комнату.'}
          </div>
          <Button size="sm" onClick={() => onExit()}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Вернуться к комнатам
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b px-3 py-2 space-y-1">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => void leave()} disabled={busy} title="Выйти из комнаты">
            <ArrowLeft className="h-4 w-4 mr-1" /> К комнатам
          </Button>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{room ? room.name : 'Голосовая комната'}</div>
            {room?.topic && <div className="text-xs text-muted-foreground truncate">{room.topic}</div>}
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {activeCount} в комнате
            {room && room.settings?.isPublic === false ? ' · приватная' : ''}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void load(true)}
            disabled={busy || loading}
            title="Обновить"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <div className={`flex items-center gap-2 text-xs ${mediaBadge.cls}`}>
          <span className={`h-2 w-2 rounded-full ${media.status === 'connected' ? 'bg-green-600' : 'bg-current'}`} />
          {mediaBadge.text}
          {media.status === 'error' && media.error && (
            <span className="text-muted-foreground truncate">— {media.error}</span>
          )}
          {(media.status === 'error' || media.status === 'unavailable') && (
            <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={media.retry}>
              Повторить
            </Button>
          )}
        </div>
      </div>

      <div ref={audioRef} />

      {media.needAudioTap && (
        <div className="shrink-0 flex items-center gap-2 border-b bg-amber-500/10 px-3 py-2 text-xs">
          <Volume2 className="h-4 w-4" /> Браузер приостановил звук — включите его вручную.
          <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => void media.startAudio()}>
            Включить звук
          </Button>
        </div>
      )}

      {media.status === 'unavailable' && (
        <div className="shrink-0 border-b bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
          {media.error || 'Медиа не настроены — присутствие и участники работают'}
        </div>
      )}

      <div className="flex-1 min-h-0 overflow-y-auto p-3">
        {loading && !room ? (
          <div className="text-xs text-muted-foreground">Загрузка комнаты…</div>
        ) : loadError ? (
          <div className="text-xs text-destructive">{loadError}</div>
        ) : (
          <div className="space-y-2">
            <div className="text-xs font-medium text-muted-foreground">
              Участники ({activeCount}){room && room.settings?.isPublic === false &&
                room.settings.roleIds.length > 0 && (
                  <span className="font-normal"> · доступ по ролям: {room.settings.roleIds.join(', ')}</span>
                )}
            </div>
            {members.length === 0 && (
              <div className="text-xs text-muted-foreground">В комнате пока никого нет.</div>
            )}
            <ul className="space-y-1">
              {members.map((m) => {
                const active = m.state === 'active';
                const isCreator = room?.createdBy === m.entityId;
                const peer = media.peers.find((p) => p.identity === m.entityId);
                const speaking =
                  active &&
                  (m.entityId === myId ? media.localSpeaking && !mutedSelf : peer?.speaking === true);
                return (
                  <VoiceRoomMemberRow
                    key={m.entityId}
                    m={m}
                    myId={myId}
                    isCreator={isCreator}
                    speaking={speaking}
                    canAct={canManage && active && !isCreator && m.entityId !== myId}
                    busy={busy}
                    onRoleChange={(member, role) => void changeRole(member, role)}
                    onToggleMute={(member) => void toggleMemberMute(member)}
                  />
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t px-3 py-2 flex items-center gap-2">
        <Button
          variant={mutedSelf ? 'default' : 'outline'}
          size="sm"
          onClick={() => void toggleSelfMute()}
          disabled={busy || !myActive}
          title={mutedSelf ? 'Включить свой микрофон' : 'Заглушить себя'}
        >
          {mutedSelf ? <Mic className="h-4 w-4 mr-1" /> : <MicOff className="h-4 w-4 mr-1" />}
          {mutedSelf ? 'Включить микрофон' : 'Заглушить себя'}
        </Button>
        <div className="flex-1" />
        <Button variant="destructive" size="sm" onClick={() => void leave()} disabled={busy}>
          <LogOut className="h-4 w-4 mr-1" /> Выйти
        </Button>
      </div>
    </div>
  );
}
